import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminNewsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin News';
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  const categoryDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Admin-focused news data
  const newsData = [
    {
      id: 1,
      title: 'New Admin Dashboard Features Released',
      excerpt: 'Latest updates to the admin dashboard include enhanced analytics, user management tools, and improved reporting capabilities.',
      image: '/news-admin-1.jpg',
      category: 'System Updates'
    },
    {
      id: 2,
      title: 'Security Protocol Updates for Admin Users',
      excerpt: 'Important security enhancements have been implemented for all administrative accounts and system access.',
      image: '/news-admin-2.jpg',
      category: 'Security'
    },
    {
      id: 3,
      title: 'New Staff Training Program Launch',
      excerpt: 'Comprehensive training program for new medical staff and administrators now available with certification modules.',
      image: '/news-admin-3.jpg',
      category: 'Training'
    },
    {
      id: 4,
      title: 'System Maintenance Scheduled',
      excerpt: 'Planned system maintenance will occur this weekend to upgrade server infrastructure and database systems.',
      image: '/news-admin-4.jpg',
      category: 'Maintenance'
    },
    {
      id: 5,
      title: 'New Compliance Requirements',
      excerpt: 'Updated healthcare compliance regulations require immediate attention from all administrative personnel.',
      image: '/news-admin-5.jpg',
      category: 'Compliance'
    },
    {
      id: 6,
      title: 'Staff Performance Analytics Available',
      excerpt: 'New performance tracking and analytics tools are now available for monitoring staff productivity and patient satisfaction.',
      image: '/news-admin-6.jpg',
      category: 'Analytics'
    },
    {
      id: 7,
      title: 'Emergency Response Protocol Update',
      excerpt: 'Updated emergency response procedures and contact information for all administrative staff.',
      image: '/news-admin-7.jpg',
      category: 'Emergency'
    },
    {
      id: 8,
      title: 'New Integration with Hospital Systems',
      excerpt: 'Seamless integration with major hospital management systems now enables better patient data coordination.',
      image: '/news-admin-8.jpg',
      category: 'Integration'
    },
    {
      id: 9,
      title: 'Budget Planning Workshop',
      excerpt: 'Annual budget planning workshop for department heads and administrative staff scheduled for next month.',
      image: '/news-admin-9.jpg',
      category: 'Finance'
    },
    {
      id: 10,
      title: 'Quality Assurance Program Launch',
      excerpt: 'New quality assurance program to ensure highest standards of patient care and administrative efficiency.',
      image: '/news-admin-10.jpg',
      category: 'Quality'
    },
    {
      id: 11,
      title: 'Data Backup and Recovery Updates',
      excerpt: 'Enhanced data backup systems and disaster recovery protocols have been implemented for better data protection.',
      image: '/news-admin-11.jpg',
      category: 'Data Management'
    },
    {
      id: 12,
      title: 'New Vendor Management System',
      excerpt: 'Streamlined vendor management system for medical supplies and equipment procurement now available.',
      image: '/news-admin-12.jpg',
      category: 'Procurement'
    },
    {
      id: 13,
      title: 'Administrative Policy Changes',
      excerpt: 'Important updates to administrative policies regarding patient privacy, data handling, and staff protocols.',
      image: '/news-admin-13.jpg',
      category: 'Policy'
    },
    {
      id: 14,
      title: 'Technology Upgrade Initiative',
      excerpt: 'Major technology infrastructure upgrades planned to improve system performance and user experience.',
      image: '/news-admin-14.jpg',
      category: 'Technology'
    },
    {
      id: 15,
      title: 'Annual Audit Preparation',
      excerpt: 'Preparation guidelines and requirements for the upcoming annual compliance and financial audit.',
      image: '/news-admin-15.jpg',
      category: 'Audit'
    }
  ];

  const handleReadMore = (newsId) => {
    // In a real app, this would navigate to the full article
    console.log('Reading full article:', newsId);
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
    setShowSortDropdown(false);
  };

  const handleAddSortOption = () => {
    setShowSortDropdown(!showSortDropdown);
    setShowCategoryDropdown(false);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setShowSortDropdown(false);
  };

  const handleSortRemove = () => {
    setSortBy('');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-gray-100 min-h-screen p-6">
          <div className="space-y-6">
            {/* Search Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search:
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Filter Category Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {['System Updates', 'Security', 'Training', 'Maintenance', 'Compliance', 'Analytics', 'Emergency', 'Integration', 'Finance', 'Quality', 'Data Management', 'Procurement', 'Policy', 'Technology', 'Audit'].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sort By Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By:
              </label>
              <div className="relative" ref={sortDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex items-center">
                    {sortBy === '' ? (
                      <span className="text-gray-400 text-sm">Sort Options</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {sortBy}
                        <button
                          onClick={handleSortRemove}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleAddSortOption}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Sort Option"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Sort Dropdown */}
                {showSortDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    {[
                      { value: 'date-newest', label: 'Date (Newest First)' },
                      { value: 'date-oldest', label: 'Date (Oldest First)' },
                      { value: 'title-az', label: 'Title (A-Z)' },
                      { value: 'title-za', label: 'Title (Z-A)' },
                      { value: 'category', label: 'Category' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortSelect(option.label)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Content Area - 80% */}
        <div className="w-4/5 bg-gray-100 p-6">
          <div className="grid grid-cols-5 gap-4">
            {newsData.map((news) => (
              <div key={news.id} className="bg-white border border-blue-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                {/* News Image */}
                <div className="h-32 bg-gray-200 flex items-center justify-center overflow-hidden">
                  <img
                    src="/admin_news.jpg"
                    alt="Admin News"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center hidden">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-300 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-blue-600 text-xs font-medium">ADMIN</span>
                      </div>
                      <span className="text-blue-600 text-xs">Admin News</span>
                    </div>
                  </div>
                </div>
               
                {/* News Content */}
                <div className="p-3">
                  <h3 className="text-blue-600 font-semibold text-sm mb-2 line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-xs mb-3 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <button
                    onClick={() => handleReadMore(news.id)}
                    className="w-full bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded hover:bg-blue-700 transition-colors"
                  >
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default AdminNewsPage;